/**
 * Error tracking — PostHog's, through the default posthog-js singleton that
 * `initPostHog()` (`@/lib/posthogClient`) initializes before React renders.
 *
 * Use this for a failure someone should look at: an unexpected API error, a
 * contract break, a worker crash. It lands as a `$exception` event and groups
 * into a PostHog Error Tracking issue, linked to the session replay. Routine
 * diagnostics belong in `@/lib/logger` instead — PostHog bills per
 * `$exception`, and a log line is not an issue.
 *
 * Uncaught errors and unhandled rejections need no call here: exception
 * autocapture (`capture_exceptions` in `@/lib/posthogClient`) reports them.
 *
 * Both functions no-op until PostHog has loaded, so they are safe in tests and
 * in the builds that ship no project token (demo, PR preview), and they no-op
 * in local `pnpm dev:local` (see `errorTrackingEnabled`).
 *
 * A thin module rather than `posthog.captureException` at each call site so
 * tests can mock one import, so hooks that already hold `usePostHog()` do not
 * need a second, shadowing `posthog` binding, and so non-Error rejections are
 * made readable in one place (see `captureException`).
 */
import posthog, { type Properties } from 'posthog-js';
import { apiErrorMessage } from '@/utils/apiErrorMessage';

/**
 * False in local `pnpm dev:local` (mode `development`): every developer's dev
 * server points at the shared dev PostHog project, where each `$exception` is
 * billed and would drown the deployed dev environment's issues. Sentry was off
 * in that mode for the same reason. `dev-stage` — deployed dev — reports.
 *
 * A function, not a constant, so a test can stub `MODE`; in a build the read
 * is a literal and folds away.
 */
export function errorTrackingEnabled(): boolean {
  return import.meta.env.MODE !== 'development';
}

function active(): boolean {
  return errorTrackingEnabled() && posthog.__loaded;
}

/** At most this much of an unrecognised object is kept as the message. */
const MAX_DESCRIBED_LENGTH = 500;

/** A bounded JSON rendering, for a thrown value with no readable message. */
export function describeError(value: unknown): string {
  try {
    const json = JSON.stringify(value);
    if (json) return json.slice(0, MAX_DESCRIBED_LENGTH);
  } catch {
    // Cyclic or otherwise unserialisable — fall through to String().
  }
  return String(value);
}

/**
 * Turn what was thrown into an `Error` PostHog can report readably.
 *
 * The generated API client rejects with the response BODY — `{ detail }`,
 * `{ statusCode, message }` — not an `Error`, and PostHog reduces a plain
 * object to its key names, which drops the server's detail and the status.
 * So a non-Error becomes an `Error` carrying `apiErrorMessage`'s reading of
 * it, and `status` and `detail` ride along as event properties.
 */
function toReportable(error: unknown): {
  error: unknown;
  properties: Properties;
} {
  if (error instanceof Error) return { error, properties: {} };
  const properties: Properties = {};
  if (typeof error === 'object' && error !== null) {
    const rec = error as Record<string, unknown>;
    const status = rec.status ?? rec.statusCode;
    if (status !== undefined) properties.status = status;
    if (rec.detail !== undefined) {
      properties.detail =
        typeof rec.detail === 'string' ? rec.detail : describeError(rec.detail);
    }
  }
  const message = apiErrorMessage(error) || describeError(error);
  return { error: new Error(message), properties };
}

/**
 * Report an exception. `properties` are flat event properties, filterable in
 * Error Tracking — name the operation with an `action` key, as every existing
 * call does. Pass `$exception_level: 'warning'` for a degraded-but-handled
 * outcome; the default level is `error`. Caller properties win over the
 * `status`/`detail` derived from a non-Error.
 */
export function captureException(error: unknown, properties?: Properties) {
  if (!active()) return;
  const reportable = toReportable(error);
  posthog.captureException(reportable.error, {
    ...reportable.properties,
    ...properties,
  });
}

/**
 * Record a step that is attached to the NEXT captured exception (PostHog's
 * equivalent of a breadcrumb). Held in memory only — sends nothing, and costs
 * nothing, unless an exception follows. Guarded like `captureException`: the
 * SDK buffers steps even before `init()`, so in a build that never initializes
 * they would sit in a buffer nothing ever sends.
 */
export function addExceptionStep(message: string, properties?: Properties) {
  if (!active()) return;
  posthog.addExceptionStep(message, properties);
}
